import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import PageShell from "../components/layout/PageShell";
import {
    getModule,
    updateModuleItems,
    generateModuleItems,
    getDeploymentStatus,
    getCrmProjectDetail,
    suggestItemImage,
    improveItem,
    redeployProject,
    getModuleItems,
    getModuleItem
} from "../services/crmService";

/* =========================================================
   CRM Module Editor - Table First UX
   Drop-in replacement for existing CrmModuleEditorPage.jsx
   ========================================================= */

function parseJson(value) {
    try {
        return typeof value === "string" ? JSON.parse(value || "{}") : value || {};
    } catch {
        return {};
    }
}

function safeString(value) {
    return value === undefined || value === null ? "" : String(value);
}

function normalizeTextKey(value = "") {
    return safeString(value)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function titleCase(value = "") {
    return safeString(value)
        .replace(/([A-Z])/g, " $1")
        .replace(/[_-]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, c => c.toUpperCase());
}

function makeItemKey(moduleKey) {
    return `${moduleKey}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatDateTime(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return "-";
    }
}

function getFieldKey(field) {
    return field?.key || field?.Key || field?.fieldKey || field?.FieldKey || field?.name || field?.Name || "";
}

function getFieldLabel(field) {
    return (
        field?.label ||
        field?.Label ||
        field?.displayName ||
        field?.DisplayName ||
        titleCase(getFieldKey(field)) ||
        "Field"
    );
}

function getFieldType(field) {
    return safeString(field?.type || field?.Type || field?.fieldType || field?.FieldType || "text").toLowerCase();
}

function getFriendlyFieldGroup(fieldKey = "") {
    const key = safeString(fieldKey).toLowerCase();

    if (["title", "name", "subtitle", "badge", "slug"].includes(key)) return "Basic Details";
    if (["description", "bio", "summary", "content", "story", "intro"].includes(key)) return "Content";
    if ([
        "price",
        "pricelabel",
        "discountlabel",
        "stocklabel",
        "ratinglabel",
        "category",
        "ctatext",
        "agegroup",
        "schedule",
        "venue",
        "duration",
        "level",
        "location",
        "area",
        "city",
        "address"
    ].includes(key)) return "Business Info";
    if (["imageurl", "image", "thumbnail", "photo", "icon"].includes(key)) return "Media";

    return "More Details";
}

function getPlaceholder(fieldKey = "", moduleKey = "") {
    const key = safeString(fieldKey).toLowerCase();
    const mod = safeString(moduleKey).toLowerCase();

    if (mod.includes("product")) {
        if (key === "title") return "Premium Cotton Shirt";
        if (key === "subtitle") return "Breathable everyday essential";
        if (key === "description") return "Short customer-friendly product description...";
        if (key === "pricelabel" || key === "price") return "₹999";
        if (key === "discountlabel") return "New Arrival";
        if (key === "stocklabel") return "Available";
        if (key === "ratinglabel") return "4.8/5";
        if (key === "category") return "Women / Men / Kids";
    }

    if (mod.includes("listing") || mod.includes("property")) {
        if (key === "title") return "Premium 3 BHK Apartment";
        if (key === "location") return "Satellite, Ahmedabad";
        if (key === "pricelabel" || key === "price") return "₹85 Lac";
    }

    if (mod.includes("program")) {
        if (key === "title") return "Beginner Football Program";
        if (key === "subtitle") return "Foundation training for young players";
        if (key === "agegroup") return "Age 8-12";
        if (key === "schedule") return "Mon, Wed, Fri";
        if (key === "venue") return "Main Ground";
        if (key === "price") return "₹2,999/month";
    }

    if (key.includes("image")) return "https://...";
    if (key === "description") return "Write a clear, customer-friendly description...";

    return `Enter ${titleCase(fieldKey)}`;
}

function readItemRaw(item) {
    return {
        ...parseJson(item?.structuredDataJson),
        ...parseJson(item?.dataJson)
    };
}

function itemTitle(item, index = 0) {
    const raw = readItemRaw(item);
    return item?.title || raw.title || raw.name || `Untitled ${index + 1}`;
}

function itemSubtitle(item) {
    const raw = readItemRaw(item);
    return (
        item?.subtitle ||
        raw.subtitle ||
        raw.category ||
        raw.location ||
        raw.ageGroup ||
        raw.badge ||
        raw.type ||
        ""
    );
}

function itemDescription(item) {
    const raw = readItemRaw(item);
    return item?.description || raw.description || raw.summary || "";
}

function itemImage(item) {
    const raw = readItemRaw(item);
    return item?.imageUrl || raw.imageUrl || raw.image || raw.thumbnail || raw.photo || "";
}

function itemPrice(item) {
    const raw = readItemRaw(item);
    return raw.priceLabel || raw.price || raw.priceRange || raw.fee || "";
}

function normalizeGeneratedModuleItem(item, moduleKey, index) {
    const raw = readItemRaw(item);

    const title = item?.title || raw.title || raw.name || `AI Suggestion ${index + 1}`;
    const slug = item?.slug || raw.slug || normalizeTextKey(title);

    const mergedRaw = {
        ...raw,
        title,
        slug,
        subtitle: item?.subtitle || raw.subtitle || "",
        description: item?.description || raw.description || "",
        badge: item?.badge || raw.badge || "",
        imageUrl: item?.imageUrl || raw.imageUrl || ""
    };

    return {
        ...item,
        id: undefined,
        itemKey: item?.itemKey || `${moduleKey}-${slug}-${Date.now()}-${index}`,
        slug,
        title,
        subtitle: mergedRaw.subtitle || "",
        description: mergedRaw.description || "",
        badge: mergedRaw.badge || "",
        imageUrl: mergedRaw.imageUrl || "",
        sortOrder: index + 1,
        isPublished: item?.isPublished !== false,
        isAiGenerated: true,
        isUserEdited: true,
        sourceType: "ai-suggestion",
        structuredDataJson: JSON.stringify(mergedRaw)
    };
}

function mergeUniqueModuleItems(existingItems, generatedItems, moduleKey) {
    const seen = new Set();

    const existing = (existingItems || []).map((item, index) => {
        const raw = readItemRaw(item);
        const title = item?.title || raw.title || raw.name || `Item ${index + 1}`;
        const slug = item?.slug || raw.slug || normalizeTextKey(title);

        seen.add(normalizeTextKey(item?.itemKey || slug || title));
        seen.add(normalizeTextKey(title));

        return {
            ...item,
            title,
            slug,
            sortOrder: index + 1
        };
    });

    const additions = [];

    (generatedItems || []).forEach((item, index) => {
        const normalized = normalizeGeneratedModuleItem(item, moduleKey, existing.length + index);
        const duplicateKey = normalizeTextKey(normalized.slug || normalized.itemKey || normalized.title);
        const titleKey = normalizeTextKey(normalized.title);

        if (seen.has(duplicateKey) || seen.has(titleKey)) {
            return;
        }

        seen.add(duplicateKey);
        seen.add(titleKey);
        additions.push(normalized);
    });

    return {
        merged: [...existing, ...additions].map((item, index) => ({
            ...item,
            sortOrder: index + 1
        })),
        addedCount: additions.length,
        firstAddedIndex: existing.length
    };
}

function normalizeItemsForSave(items, moduleKey) {
    return (items || []).map((item, index) => {
        const raw = readItemRaw(item);
        const title = item.title || raw.title || raw.name || `Item ${index + 1}`;
        const slug = item.slug || raw.slug || normalizeTextKey(title);

        const normalizedRaw = {
            ...raw,
            title,
            slug,
            subtitle: item.subtitle || raw.subtitle || "",
            description: item.description || raw.description || "",
            badge: item.badge || raw.badge || "",
            imageUrl: item.imageUrl || raw.imageUrl || ""
        };

        const normalized = {
            ...item,
            itemKey: item.itemKey || item.ItemKey || makeItemKey(moduleKey),
            slug,
            title,
            subtitle: normalizedRaw.subtitle,
            description: normalizedRaw.description,
            badge: normalizedRaw.badge || null,
            imageUrl: normalizedRaw.imageUrl || null,
            sortOrder: index + 1,
            isPublished: item.isPublished !== false,
            isUserEdited: true,
            structuredDataJson: JSON.stringify(normalizedRaw)
        };

        if (!normalized.id || normalized.id === "00000000-0000-0000-0000-000000000000") {
            delete normalized.id;
        }

        delete normalized.isAiGenerated;
        delete normalized.sourceType;
        delete normalized.dataJson;

        return normalized;
    });
}

function buildDefaultAiInstruction(moduleKey, project, items) {
    const existingTitles = (items || [])
        .map((item, index) => itemTitle(item, index))
        .filter(Boolean)
        .slice(0, 12)
        .join(", ");

    const industry = project?.industry || "";
    const subIndustry = project?.subIndustry || "";

    return [
        `Generate fresh, non-duplicate ${moduleKey} options.`,
        industry ? `Industry: ${industry}.` : "",
        subIndustry ? `Sub-industry: ${subIndustry}.` : "",
        existingTitles ? `Avoid repeating these existing items: ${existingTitles}.` : "",
        "Make suggestions practical, customer-facing, and suitable for a generated business website."
    ].filter(Boolean).join(" ");
}

function csvToObjects(text) {
    const rows = safeString(text)
        .split(/\r?\n/)
        .map(x => x.trim())
        .filter(Boolean);

    if (rows.length < 2) return [];

    const headers = rows[0].split(",").map(x => x.trim());

    return rows.slice(1).map(row => {
        const values = row.split(",").map(x => x.trim());
        const item = {};
        headers.forEach((header, index) => {
            if (header) item[header] = values[index] || "";
        });
        return item;
    });
}

function getDeploymentTone(status) {
    const value = status?.status || "idle";

    if (value === "failed") return "failed";
    if (value === "live") return "live";
    if (value === "syncing" || value === "redeploying") return "running";
    if (value === "synced") return "synced";

    return "idle";
}

function DeploymentCompact({ status, saving, onRefresh }) {
    const tone = saving ? "running" : getDeploymentTone(status);
    const label = saving ? "Saving changes..." : status?.label || "No active deployment";
    const statusText = saving ? "saving" : status?.status || "idle";

    return (
        <div className={`module-deployment-compact ${tone}`}>
            <div>
                <span>Deployment</span>
                <strong>{label}</strong>
            </div>

            <div className="module-deployment-compact-grid">
                <small>Status: {statusText}</small>
                <small>Sync: {status?.syncStatus || "-"}</small>
                <small>Redeploy: {status?.redeployStatus || "-"}</small>
                <small>Last live: {formatDateTime(status?.lastRedeployCompletedAtUtc)}</small>
            </div>

            <button type="button" className="project-action-btn compact" onClick={onRefresh}>
                Refresh
            </button>
        </div>
    );
}

function ConfirmModal({
    open,
    title,
    message,
    confirmLabel = "Continue",
    cancelLabel = "Cancel",
    tone = "default",
    onConfirm,
    onCancel
}) {
    if (!open) return null;

    return (
        <div className="project-modal-backdrop">
            <div className="project-modal confirm-action-modal">
                <div className="project-modal-header">
                    <div>
                        <span className="module-editor-kicker">
                            {tone === "danger" ? "Please Confirm" : "Before You Continue"}
                        </span>
                        <h2>{title}</h2>
                        <p>{message}</p>
                    </div>

                    <button type="button" onClick={onCancel}>
                        ×
                    </button>
                </div>

                <div className="confirm-action-footer">
                    <button type="button" className="project-action-btn" onClick={onCancel}>
                        {cancelLabel}
                    </button>

                    <button
                        type="button"
                        className={`project-action-btn ${tone === "danger" ? "danger" : "primary"}`}
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function CrmModuleEditorPage() {
    const { projectId, moduleKey } = useParams();

    const [module, setModule] = useState(null);
    const [project, setProject] = useState(null);
    const [items, setItems] = useState([]);
    const [fields, setFields] = useState([]);

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [activeView, setActiveView] = useState("table");
    const [editorMode, setEditorMode] = useState("edit");

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [suggestingImage, setSuggestingImage] = useState(false);

    const [search, setSearch] = useState("");
    const [visibilityFilter, setVisibilityFilter] = useState("all");
    const [bulkType, setBulkType] = useState("json");
    const [bulkText, setBulkText] = useState("");
    const [aiInstruction, setAiInstruction] = useState("");

    const [deploymentStatus, setDeploymentStatus] = useState(null);
    const [message, setMessage] = useState("");
    const [confirmDialog, setConfirmDialog] = useState(null);

    const deploymentCompletionMessageShownRef = useRef(false);

    const selectedItem = items[selectedIndex];
    const selectedRaw = readItemRaw(selectedItem);

    const enrichedItems = useMemo(() => {
        return (items || []).map((item, index) => ({
            item,
            raw: readItemRaw(item),
            index,
            title: itemTitle(item, index),
            subtitle: itemSubtitle(item),
            description: itemDescription(item),
            imageUrl: itemImage(item),
            price: itemPrice(item),
            isPublished: item?.isPublished !== false
        }));
    }, [items]);

    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();

        return enrichedItems.filter(row => {
            const haystack = [
                row.title,
                row.subtitle,
                row.description,
                row.price,
                row.raw.category,
                row.raw.location,
                row.raw.badge
            ].join(" ").toLowerCase();

            const matchesSearch = !q || haystack.includes(q);

            const matchesVisibility =
                visibilityFilter === "all" ||
                (visibilityFilter === "published" && row.isPublished) ||
                (visibilityFilter === "hidden" && !row.isPublished) ||
                (visibilityFilter === "missing-image" && !row.imageUrl);

            return matchesSearch && matchesVisibility;
        });
    }, [enrichedItems, search, visibilityFilter]);

    const groupedFields = useMemo(() => {
        return fields
            .filter(f => getFieldKey(f))
            .reduce((acc, field) => {
                const key = getFieldKey(field);
                const group = getFriendlyFieldGroup(key);
                acc[group] = acc[group] || [];
                acc[group].push(field);
                return acc;
            }, {});
    }, [fields]);

    const selectedQuality = useMemo(() => {
        const filled = Object.keys(selectedRaw || {}).filter(k => {
            const value = selectedRaw[k];
            if (Array.isArray(value)) return value.length > 0;
            return value !== undefined && value !== null && safeString(value).trim() !== "";
        }).length;

        return {
            filled,
            hasImage: !!itemImage(selectedItem),
            isPublished: selectedItem?.isPublished !== false
        };
    }, [selectedRaw, selectedItem]);

    const showMessage = (text) => {
        setMessage(text);

        if (!safeString(text).toLowerCase().includes("failed")) {
            setTimeout(() => setMessage(""), 3500);
        }
    };

    const markDirty = () => {
        setDirty(true);
        setMessage("");
    };

    const loadDeploymentStatus = useCallback(async () => {
        try {
            const data = await getDeploymentStatus(projectId);
            setDeploymentStatus(data);
            return data;
        } catch (err) {
            console.error(err);
            return null;
        }
    }, [projectId]);

    const load = useCallback(async () => {
        try {
            setLoading(true);

            const [moduleData, itemSummaries, projectData] = await Promise.all([
                getModule(projectId, moduleKey),      // keep only for schema/module metadata
                getModuleItems(projectId, moduleKey), // new lightweight table data
                getCrmProjectDetail(projectId)
            ]);

            setItems(itemSummaries || []);

            setProject(projectData);
            setModule(moduleData || {});

            const schema = parseJson(moduleData?.schemaJson);
            const schemaFields = schema?.fields || [];

            if (schemaFields.length > 0) {
                setFields(schemaFields);
            } else {
                const firstItem = (moduleData?.items || [])[0];
                const raw = readItemRaw(firstItem);

                const fallbackFields = Object.keys(raw || {})
                    .filter(key => !["id", "itemKey", "slug"].includes(key))
                    .map(key => ({
                        key,
                        label: titleCase(key),
                        type: key.toLowerCase().includes("description") ? "textarea" : "text"
                    }));

                setFields(fallbackFields);
            }
            setSelectedIndex(0);
            setActiveView("table");
            setDirty(false);

            await loadDeploymentStatus();
        } catch (err) {
            console.error(err);
            setMessage("Failed to load module.");
        } finally {
            setLoading(false);
        }
    }, [projectId, moduleKey, loadDeploymentStatus]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!deploymentStatus || deploymentStatus.isTerminal) return;

        const timer = setInterval(() => {
            loadDeploymentStatus();
        }, 2500);

        return () => clearInterval(timer);
    }, [deploymentStatus, loadDeploymentStatus]);

    useEffect(() => {
        const current = deploymentStatus?.status || "idle";

        if (current === "failed") {
            deploymentCompletionMessageShownRef.current = false;
            showMessage("Deployment failed. Please review deployment status.");
            return;
        }

        if (current === "live" && !saving && !deploymentCompletionMessageShownRef.current) {
            deploymentCompletionMessageShownRef.current = true;
            showMessage("Deployment completed. Live website is updated.");
        }
    }, [deploymentStatus?.status, saving]);

    const openEditor = async (index) => {
        const summary = items[index];

        setSelectedIndex(index);

        if (summary?.id) {
            const full = await getModuleItem(projectId, moduleKey, summary.id);

            setItems(prev => {
                const next = [...prev];
                next[index] = full;
                return next;
            });
        }

        setActiveView("editor");
        setEditorMode("edit");
    };

    const updateItem = (index, key, value) => {
        setItems(prev => {
            const next = [...prev];
            const current = next[index] || {};
            const raw = readItemRaw(current);

            raw[key] = value;

            next[index] = {
                ...current,
                structuredDataJson: JSON.stringify(raw),
                title: key === "title" || key === "name" ? value : current.title,
                subtitle: key === "subtitle" ? value : current.subtitle,
                description: key === "description" ? value : current.description,
                badge: key === "badge" ? value : current.badge,
                imageUrl: key === "imageUrl" ? value : current.imageUrl,
                isUserEdited: true
            };

            return next;
        });

        markDirty();
    };

    const updateSelectedJson = (text) => {
        try {
            const parsed = JSON.parse(text);
            setItems(prev => {
                const next = [...prev];
                next[selectedIndex] = {
                    ...next[selectedIndex],
                    structuredDataJson: JSON.stringify(parsed),
                    title: parsed.title || parsed.name || next[selectedIndex]?.title,
                    subtitle: parsed.subtitle || next[selectedIndex]?.subtitle,
                    description: parsed.description || next[selectedIndex]?.description,
                    imageUrl: parsed.imageUrl || next[selectedIndex]?.imageUrl,
                    isUserEdited: true
                };
                return next;
            });
            markDirty();
        } catch {
            // Allow user to type invalid JSON temporarily.
        }
    };

    const addItem = () => {
        const raw = {};
        fields.forEach(field => {
            const fieldKey = getFieldKey(field);
            if (fieldKey) raw[fieldKey] = "";
        });

        const newItem = {
            itemKey: makeItemKey(moduleKey),
            title: "",
            subtitle: "",
            description: "",
            imageUrl: "",
            sortOrder: items.length + 1,
            isPublished: true,
            isUserEdited: true,
            structuredDataJson: JSON.stringify(raw)
        };

        setItems(prev => [...prev, newItem]);
        setSelectedIndex(items.length);
        setActiveView("editor");
        setEditorMode("edit");
        markDirty();
    };

    const duplicateItem = (index) => {
        const item = items[index];
        if (!item) return;

        const raw = readItemRaw(item);
        const title = itemTitle(item, index);

        const copyRaw = {
            ...raw,
            title: `${title} Copy`
        };

        const duplicated = {
            ...item,
            id: undefined,
            itemKey: makeItemKey(moduleKey),
            title: `${title} Copy`,
            sortOrder: index + 2,
            isUserEdited: true,
            structuredDataJson: JSON.stringify(copyRaw)
        };

        setItems(prev => {
            const next = [...prev];
            next.splice(index + 1, 0, duplicated);
            return next.map((x, i) => ({ ...x, sortOrder: i + 1 }));
        });

        setSelectedIndex(index + 1);
        setActiveView("editor");
        setEditorMode("edit");
        markDirty();
    };

    const deleteItem = (index) => {
        const item = items[index];
        const label = itemTitle(item, index);

        setConfirmDialog({
            title: `Delete "${label}"?`,
            message: "This removes the item locally. Click Save & Publish to update the live website.",
            confirmLabel: "Delete Item",
            cancelLabel: "Keep Item",
            tone: "danger",
            onConfirm: () => {
                const updated = items
                    .filter((_, i) => i !== index)
                    .map((x, i) => ({ ...x, sortOrder: i + 1 }));

                setItems(updated);
                setSelectedIndex(Math.max(0, Math.min(index, updated.length - 1)));
                setConfirmDialog(null);
                markDirty();
                showMessage("Item removed locally. Click Save & Publish to update the live website.");
            }
        });
    };

    const togglePublish = (index) => {
        setItems(prev => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                isPublished: next[index].isPublished === false
            };
            return next;
        });

        markDirty();
    };

    const moveItem = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= items.length) return;

        setItems(prev => {
            const next = [...prev];
            const temp = next[index];
            next[index] = next[target];
            next[target] = temp;
            return next.map((x, i) => ({ ...x, sortOrder: i + 1 }));
        });

        setSelectedIndex(target);
        markDirty();
    };

    const publishAllHiddenItems = () => {
        setItems(prev =>
            prev.map(item => ({
                ...item,
                isPublished: true
            }))
        );

        markDirty();
        showMessage("All hidden items marked as published. Click Save & Publish to update the live website.");
    };

    const saveDraft = async () => {
        try {
            setSaving(true);
            setMessage("");

            const normalized = normalizeItemsForSave(items, moduleKey);
            await updateModuleItems(projectId, moduleKey, normalized, { publish: false });

            setItems(normalized);
            setDirty(false);
            showMessage("Draft saved. Live website was not changed.");
            await loadDeploymentStatus();
        } catch (err) {
            console.error(err);
            showMessage("Save draft failed.");
        } finally {
            setSaving(false);
        }
    };

    const saveAndPublish = async () => {
        try {
            deploymentCompletionMessageShownRef.current = false;
            setSaving(true);
            setMessage("");

            const normalized = normalizeItemsForSave(items, moduleKey);
            await updateModuleItems(projectId, moduleKey, normalized, { publish: true });

            setItems(normalized);
            setDirty(false);
            showMessage("Saved. Sync and redeploy started.");
            await loadDeploymentStatus();
            setTimeout(loadDeploymentStatus, 1500);
            setTimeout(loadDeploymentStatus, 3500);
            setTimeout(loadDeploymentStatus, 7000);
        } catch (err) {
            console.error(err);
            showMessage("Save & Publish failed.");
        } finally {
            setSaving(false);
        }
    };

    const manualRedeploy = async () => {
        try {
            deploymentCompletionMessageShownRef.current = false;
            setSaving(true);
            setMessage("Updating live website...");

            await redeployProject(projectId, "manual");

            await loadDeploymentStatus();
            setTimeout(loadDeploymentStatus, 1500);
            setTimeout(loadDeploymentStatus, 3500);
            setTimeout(loadDeploymentStatus, 7000);

            showMessage("Live site update started.");
        } catch (err) {
            console.error(err);
            showMessage("Live site update failed.");
        } finally {
            setSaving(false);
        }
    };

    const runAiGeneration = async (trimmedInstruction = "") => {
        try {
            setGenerating(true);
            setMessage("");

            const result = await generateModuleItems(projectId, moduleKey, {
                instruction: trimmedInstruction || buildDefaultAiInstruction(moduleKey, project, items),
                replaceExisting: false
            });

            const generated = Array.isArray(result)
                ? result
                : result?.items || result?.suggestions || [];

            if (!generated.length) {
                showMessage("AI did not return new suggestions. Try adding a more specific AI direction.");
                return;
            }

            const { merged, addedCount, firstAddedIndex } = mergeUniqueModuleItems(
                items,
                generated,
                moduleKey
            );

            if (addedCount === 0) {
                showMessage("AI suggestions matched existing items. Add a more specific AI direction.");
                return;
            }

            setItems(merged);
            setSelectedIndex(firstAddedIndex);
            setActiveView("editor");
            setEditorMode("edit");
            setDirty(true);

            showMessage(`${addedCount} AI suggestion${addedCount === 1 ? "" : "s"} added.`);
        } catch (err) {
            console.error(err);
            showMessage("AI generation failed.");
        } finally {
            setGenerating(false);
        }
    };

    const generateWithAI = async () => {
        const hasExistingItems = items.length > 0;
        const trimmedInstruction = aiInstruction.trim();

        if (hasExistingItems && !trimmedInstruction) {
            setConfirmDialog({
                title: "Generate generic AI suggestions?",
                message: "This module already has content. Adding AI Direction improves quality and avoids duplicates.",
                confirmLabel: "Generate Anyway",
                cancelLabel: "Add Direction First",
                onConfirm: async () => {
                    setConfirmDialog(null);
                    await runAiGeneration(trimmedInstruction);
                }
            });
            return;
        }

        await runAiGeneration(trimmedInstruction);
    };

    const handleImproveItem = async () => {
        if (!selectedItem) return;

        try {
            deploymentCompletionMessageShownRef.current = false;
            setSaving(true);
            setMessage("Improving selected item...");

            const improved = await improveItem(projectId, moduleKey, selectedRaw);
            if (!improved) return;

            const updatedItems = [...items];
            updatedItems[selectedIndex] = {
                ...updatedItems[selectedIndex],
                ...improved,
                title: improved.title || improved.name || updatedItems[selectedIndex]?.title,
                subtitle: improved.subtitle || updatedItems[selectedIndex]?.subtitle,
                description: improved.description || updatedItems[selectedIndex]?.description,
                imageUrl: improved.imageUrl || updatedItems[selectedIndex]?.imageUrl,
                structuredDataJson: JSON.stringify(improved),
                isUserEdited: true
            };

            setItems(updatedItems);

            const normalized = normalizeItemsForSave(updatedItems, moduleKey);
            await updateModuleItems(projectId, moduleKey, normalized, { publish: true });

            setItems(normalized);
            setDirty(false);
            showMessage("AI improved and published. Website updating...");
            await loadDeploymentStatus();
        } catch (err) {
            console.error(err);
            showMessage("AI improve failed.");
        } finally {
            setSaving(false);
        }
    };

    const handleSuggestImage = async () => {
        if (!selectedItem) {
            showMessage("Select an item first.");
            return;
        }

        try {
            setSuggestingImage(true);
            setMessage("Finding image...");

            const raw = readItemRaw(selectedItem);

            const payload = {
                ...raw,
                title: selectedItem?.title || raw.title || raw.name || "",
                subtitle: selectedItem?.subtitle || raw.subtitle || "",
                description: selectedItem?.description || raw.description || "",
                badge: selectedItem?.badge || raw.badge || "",
                currentImageUrl: selectedItem?.imageUrl || raw.imageUrl || "",
                moduleKey,
                industry: project?.industry || "",
                subIndustry: project?.subIndustry || "",
                businessName: project?.businessName || ""
            };

            const result = await suggestItemImage(projectId, moduleKey, payload);

            const imageUrl =
                result?.imageUrl ||
                result?.url ||
                result?.blobUrl ||
                result?.publicUrl ||
                "";

            if (!imageUrl) {
                showMessage("Image suggestion did not return an image URL.");
                return;
            }

            setItems(prev => {
                const next = [...prev];
                const current = next[selectedIndex] || {};
                const currentRaw = readItemRaw(current);

                const updatedRaw = {
                    ...currentRaw,
                    imageUrl
                };

                next[selectedIndex] = {
                    ...current,
                    imageUrl,
                    structuredDataJson: JSON.stringify(updatedRaw),
                    isUserEdited: true
                };

                return next;
            });

            setEditorMode("preview");
            setDirty(true);
            showMessage("Image suggested. Review and Save & Publish.");
        } catch (err) {
            console.error(err);
            showMessage("Image suggestion failed.");
        } finally {
            setSuggestingImage(false);
        }
    };

    const openBulkImport = () => {
        setBulkType("json");
        setBulkText(JSON.stringify(items.map((item, index) => {
            const raw = readItemRaw(item);
            return {
                ...raw,
                title: itemTitle(item, index),
                subtitle: itemSubtitle(item),
                description: itemDescription(item),
                imageUrl: itemImage(item),
                isPublished: item.isPublished !== false
            };
        }), null, 2));
        setActiveView("bulk");
    };

    const applyBulkImport = () => {
        try {
            let parsed = [];

            if (bulkType === "json") {
                parsed = JSON.parse(bulkText);
            } else {
                parsed = csvToObjects(bulkText);
            }

            if (!Array.isArray(parsed)) {
                showMessage("Bulk import must be an array or CSV rows.");
                return;
            }

            const normalized = parsed.map((raw, index) => {
                const title = raw.title || raw.name || `Item ${index + 1}`;
                const slug = raw.slug || normalizeTextKey(title);

                return {
                    itemKey: makeItemKey(moduleKey),
                    slug,
                    title,
                    subtitle: raw.subtitle || "",
                    description: raw.description || "",
                    badge: raw.badge || "",
                    imageUrl: raw.imageUrl || "",
                    sortOrder: index + 1,
                    isPublished: raw.isPublished !== false,
                    isUserEdited: true,
                    structuredDataJson: JSON.stringify({
                        ...raw,
                        title,
                        slug
                    })
                };
            });

            setItems(normalized);
            setSelectedIndex(0);
            setDirty(true);
            setActiveView("table");
            showMessage(`${normalized.length} item(s) imported. Review and Save Draft or Save & Publish.`);
        } catch (err) {
            console.error(err);
            showMessage("Bulk import failed. Please check format.");
        }
    };

    if (loading) {
        return <PageShell title="Loading module..." />;
    }

    return (
        <PageShell title={module?.moduleLabel || "Module Editor"}>
            <div className="module-premium-shell module-manager-shell">
                <section className="module-manager-header">
                    <div>
                        <Link className="module-back-link" to={`/crm/projects/${projectId}`}>
                            ← Back to Project
                        </Link>

                        <span className="module-editor-kicker">CRM Content Manager</span>

                        <h1>
                            {module?.moduleLabel || titleCase(moduleKey)}
                            {dirty && <span className="unsaved-badge">Unsaved</span>}
                        </h1>

                        <p>
                            Manage website content in a faster table-first workspace. Edit, import,
                            reorder, publish, and update the live website from one place.
                        </p>

                        <div className="module-manager-stats">
                            <span>{items.length} items</span>
                            <span>{items.filter(x => x.isPublished !== false).length} live</span>
                            <span>{items.filter(x => x.isPublished === false).length} hidden</span>
                            <span>{items.filter(x => !itemImage(x)).length} missing images</span>
                        </div>
                    </div>

                    <div className="module-manager-actions">
                        <button className="project-action-btn" onClick={generateWithAI} disabled={generating || saving}>
                            {generating ? "Generating..." : "✨ Add AI Suggestions"}
                        </button>

                        <button className="project-action-btn" onClick={openBulkImport}>
                            Bulk Import
                        </button>

                        <button className="project-action-btn primary" onClick={saveDraft} disabled={saving || generating || !dirty}>
                            {saving ? "Saving..." : "Save Draft"}
                        </button>

                        <button className="project-action-btn primary" onClick={saveAndPublish} disabled={saving || generating}>
                            {saving ? "Publishing..." : "Save & Publish"}
                        </button>

                        <button className="project-action-btn" onClick={manualRedeploy} disabled={saving || generating}>
                            Update Live Site
                        </button>

                        {project?.liveUrl && (
                            <a className="project-action-btn" href={project.liveUrl} target="_blank" rel="noreferrer">
                                View Live ↗
                            </a>
                        )}
                    </div>
                </section>

                <DeploymentCompact
                    status={deploymentStatus}
                    saving={saving}
                    onRefresh={loadDeploymentStatus}
                />

                {message && (
                    <div className={`module-toast ${message.toLowerCase().includes("failed") ? "error" : "success"}`}>
                        {message}
                    </div>
                )}

                <section className="module-manager-commandbar">
                    <div className="module-manager-tabs">
                        <button
                            className={activeView === "table" ? "active" : ""}
                            onClick={() => setActiveView("table")}
                        >
                            Table
                        </button>
                        <button
                            className={activeView === "editor" ? "active" : ""}
                            onClick={() => setActiveView("editor")}
                            disabled={!items.length}
                        >
                            Edit Selected
                        </button>
                        <button
                            className={activeView === "bulk" ? "active" : ""}
                            onClick={openBulkImport}
                        >
                            Bulk Import
                        </button>
                    </div>

                    <div className="module-manager-search">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search title, category, location, description..."
                        />

                        <select
                            value={visibilityFilter}
                            onChange={(e) => setVisibilityFilter(e.target.value)}
                        >
                            <option value="all">All Items</option>
                            <option value="published">Published</option>
                            <option value="hidden">Hidden</option>
                            <option value="missing-image">Missing Image</option>
                        </select>
                    </div>

                    <div className="module-ai-command module-manager-ai-command">
                        <span>AI Direction</span>
                        <input
                            value={aiInstruction}
                            onChange={(e) => setAiInstruction(e.target.value)}
                            placeholder="Example: premium fashion products, avoid furniture, add prices..."
                        />
                    </div>
                </section>

                {items.length === 0 && activeView !== "bulk" ? (
                    <section className="module-empty-premium">
                        <div className="module-empty-icon">✦</div>
                        <h2>No items yet</h2>
                        <p>Add your first item, import items, or generate starter suggestions with AI.</p>
                        <div className="module-edit-actions">
                            <button className="project-action-btn primary" onClick={addItem}>
                                + Add First Item
                            </button>
                            <button className="project-action-btn" onClick={openBulkImport}>
                                Bulk Import
                            </button>
                            <button className="project-action-btn" onClick={generateWithAI} disabled={generating}>
                                {generating ? "Generating..." : "✨ AI Generate"}
                            </button>
                        </div>
                    </section>
                ) : null}

                {activeView === "table" && items.length > 0 && (
                    <section className="module-manager-table-card">
                        <div className="module-manager-table-head">
                            <div>
                                <span className="module-editor-kicker">Content Table</span>
                                <h2>{filteredItems.length} item(s)</h2>
                                <p>Select a row to edit. Use quick actions for publish, duplicate, reorder, or delete.</p>
                            </div>

                            <div className="module-manager-table-actions">
                                <button className="project-action-btn primary" onClick={addItem}>
                                    + Add Item
                                </button>

                                <button
                                    className="project-action-btn"
                                    onClick={publishAllHiddenItems}
                                    disabled={!items.some(x => x.isPublished === false)}
                                >
                                    Publish Hidden
                                </button>
                            </div>
                        </div>

                        <div className="module-manager-table-wrap">
                            <table className="module-manager-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: "74px" }}>Image</th>
                                        <th>Item</th>
                                        <th>Category / Meta</th>
                                        <th>Price</th>
                                        <th>Status</th>
                                        <th style={{ width: "250px" }}>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={6}>
                                                <div className="module-table-empty">
                                                    No items match your filter.
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredItems.map(row => (
                                            <tr
                                                key={row.item.id || row.item.itemKey || row.index}
                                                className={row.index === selectedIndex ? "active" : ""}
                                                onClick={() => openEditor(row.index)}
                                            >
                                                <td>
                                                    {row.imageUrl ? (
                                                        <img className="module-manager-thumb" src={row.imageUrl} alt="" />
                                                    ) : (
                                                        <div className="module-manager-thumb empty">No image</div>
                                                    )}
                                                </td>

                                                <td>
                                                    <div className="module-manager-item-main">
                                                        <strong>{row.title}</strong>
                                                        <span>{row.description || "No description yet."}</span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="module-manager-meta-stack">
                                                        <strong>{row.subtitle || row.raw.category || row.raw.location || "-"}</strong>
                                                        <span>{row.raw.badge || row.raw.stockLabel || row.raw.ratingLabel || ""}</span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <strong>{row.price || "-"}</strong>
                                                </td>

                                                <td>
                                                    <span className={row.isPublished ? "item-badge live" : "item-badge draft"}>
                                                        {row.isPublished ? "Live" : "Hidden"}
                                                    </span>
                                                </td>

                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div className="module-row-actions">
                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact"
                                                            onClick={() => openEditor(row.index)}
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact"
                                                            onClick={() => togglePublish(row.index)}
                                                        >
                                                            {row.isPublished ? "Hide" : "Publish"}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact"
                                                            onClick={() => duplicateItem(row.index)}
                                                        >
                                                            Copy
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact"
                                                            onClick={() => moveItem(row.index, -1)}
                                                        >
                                                            ↑
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact"
                                                            onClick={() => moveItem(row.index, 1)}
                                                        >
                                                            ↓
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact danger"
                                                            onClick={() => deleteItem(row.index)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {activeView === "editor" && selectedItem && (
                    <section className="module-manager-editor-grid">
                        <aside className="module-selected-summary">
                            <div className="module-selected-preview">
                                {itemImage(selectedItem) ? (
                                    <img src={itemImage(selectedItem)} alt="" />
                                ) : (
                                    <div className="module-selected-preview-empty">No image</div>
                                )}

                                <span>{selectedItem?.badge || selectedRaw.badge || selectedRaw.category || "Selected Item"}</span>
                                <h2>{itemTitle(selectedItem, selectedIndex)}</h2>
                                <p>{itemDescription(selectedItem) || "Add a clear description to improve the live website content."}</p>
                            </div>

                            <div className="module-quality-strip vertical">
                                <div>
                                    <strong>{selectedQuality.filled}</strong>
                                    <span>Filled fields</span>
                                </div>
                                <div>
                                    <strong>{selectedQuality.hasImage ? "Yes" : "No"}</strong>
                                    <span>Image</span>
                                </div>
                                <div>
                                    <strong>{selectedQuality.isPublished ? "Live" : "Hidden"}</strong>
                                    <span>Visibility</span>
                                </div>
                            </div>

                            <div className="module-edit-actions vertical">
                                <button className="project-action-btn" onClick={handleImproveItem} disabled={saving || generating}>
                                    ✨ Improve & Publish
                                </button>

                                <button className="project-action-btn" onClick={handleSuggestImage} disabled={saving || generating || suggestingImage}>
                                    {suggestingImage ? "Finding image..." : "🖼 Suggest Image"}
                                </button>

                                <button className="project-action-btn" onClick={() => togglePublish(selectedIndex)}>
                                    {selectedItem?.isPublished === false ? "Publish Item" : "Hide Item"}
                                </button>

                                <button className="project-action-btn" onClick={() => duplicateItem(selectedIndex)}>
                                    Duplicate
                                </button>

                                <button className="project-action-btn danger" onClick={() => deleteItem(selectedIndex)}>
                                    Delete
                                </button>
                            </div>
                        </aside>

                        <main className="module-edit-panel module-manager-editor-panel">
                            <div className="module-edit-header module-edit-header-polished">
                                <div className="module-edit-title-block">
                                    <span className="module-editor-kicker">Editing Item #{selectedIndex + 1}</span>
                                    <h2>{itemTitle(selectedItem, selectedIndex)}</h2>
                                    <p>Edit details below, preview the card, or edit raw JSON for advanced changes.</p>
                                </div>

                                <div className="module-mode-tabs module-mode-tabs-polished">
                                    <button
                                        className={editorMode === "edit" ? "active" : ""}
                                        onClick={() => setEditorMode("edit")}
                                    >
                                        Edit
                                    </button>

                                    <button
                                        className={editorMode === "preview" ? "active" : ""}
                                        onClick={() => setEditorMode("preview")}
                                    >
                                        Preview
                                    </button>

                                    <button
                                        className={editorMode === "json" ? "active" : ""}
                                        onClick={() => setEditorMode("json")}
                                    >
                                        JSON
                                    </button>
                                </div>
                            </div>

                            {editorMode === "edit" && (
                                <>
                                    {Object.entries(groupedFields).map(([group, groupFields]) => (
                                        <section className="module-field-section premium" key={group}>
                                            <h4>{group}</h4>

                                            <div className="module-field-grid">
                                                {groupFields.map(field => {
                                                    const fieldKey = getFieldKey(field);
                                                    const fieldLabel = getFieldLabel(field);
                                                    const fieldType = getFieldType(field);
                                                    const val = selectedRaw[fieldKey] ?? "";

                                                    return (
                                                        <label className="module-field premium" key={fieldKey}>
                                                            <span>{fieldLabel}</span>

                                                            {fieldType === "textarea" || fieldKey.toLowerCase().includes("description") || fieldKey.toLowerCase().includes("content") ? (
                                                                <textarea
                                                                    value={val}
                                                                    rows={4}
                                                                    placeholder={getPlaceholder(fieldKey, moduleKey)}
                                                                    onChange={(e) => updateItem(selectedIndex, fieldKey, e.target.value)}
                                                                />
                                                            ) : fieldType === "boolean" ? (
                                                                <select
                                                                    value={val === true || val === "true" ? "true" : "false"}
                                                                    onChange={(e) => updateItem(selectedIndex, fieldKey, e.target.value === "true")}
                                                                >
                                                                    <option value="true">Yes</option>
                                                                    <option value="false">No</option>
                                                                </select>
                                                            ) : (
                                                                <input
                                                                    value={val}
                                                                    placeholder={getPlaceholder(fieldKey, moduleKey)}
                                                                    onChange={(e) => updateItem(selectedIndex, fieldKey, e.target.value)}
                                                                />
                                                            )}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    ))}
                                </>
                            )}

                            {editorMode === "preview" && (
                                <div className="module-canvas-preview">
                                    <div className="module-canvas-card">
                                        {itemImage(selectedItem) && (
                                            <img src={itemImage(selectedItem)} alt="" />
                                        )}

                                        <span>{selectedItem?.badge || selectedRaw.badge || "Preview"}</span>
                                        <h2>{itemTitle(selectedItem, selectedIndex)}</h2>
                                        <p>{itemDescription(selectedItem) || "Add content to preview this item."}</p>

                                        {itemPrice(selectedItem) && <strong>{itemPrice(selectedItem)}</strong>}
                                    </div>
                                </div>
                            )}

                            {editorMode === "json" && (
                                <textarea
                                    className="module-json-area"
                                    value={JSON.stringify(selectedRaw, null, 2)}
                                    onChange={(e) => updateSelectedJson(e.target.value)}
                                    rows={22}
                                />
                            )}
                        </main>
                    </section>
                )}

                {activeView === "bulk" && (
                    <section className="module-manager-bulk-card">
                        <div className="module-manager-table-head">
                            <div>
                                <span className="module-editor-kicker">Bulk Import</span>
                                <h2>Import module items</h2>
                                <p>Paste JSON array or simple CSV. This replaces the current local item list after import. Save after reviewing.</p>
                            </div>

                            <div className="module-manager-table-actions">
                                <button
                                    className={`project-action-btn ${bulkType === "json" ? "primary" : ""}`}
                                    onClick={() => setBulkType("json")}
                                >
                                    JSON
                                </button>

                                <button
                                    className={`project-action-btn ${bulkType === "csv" ? "primary" : ""}`}
                                    onClick={() => setBulkType("csv")}
                                >
                                    CSV
                                </button>
                            </div>
                        </div>

                        <textarea
                            className="module-json-area module-bulk-area"
                            value={bulkText}
                            onChange={(e) => setBulkText(e.target.value)}
                            rows={22}
                            placeholder={
                                bulkType === "json"
                                    ? '[{"title":"Blue Shirt","priceLabel":"₹999","category":"Women"}]'
                                    : "title,priceLabel,category\nBlue Shirt,₹999,Women\nBlack Jeans,₹1299,Men"
                            }
                        />

                        <div className="module-edit-actions">
                            <button className="project-action-btn primary" onClick={applyBulkImport}>
                                Apply Import
                            </button>

                            <button className="project-action-btn" onClick={() => setActiveView("table")}>
                                Back to Table
                            </button>
                        </div>
                    </section>
                )}
            </div>

            <ConfirmModal
                open={!!confirmDialog}
                title={confirmDialog?.title}
                message={confirmDialog?.message}
                confirmLabel={confirmDialog?.confirmLabel}
                cancelLabel={confirmDialog?.cancelLabel}
                tone={confirmDialog?.tone}
                onCancel={() => setConfirmDialog(null)}
                onConfirm={() => {
                    const action = confirmDialog?.onConfirm;
                    if (typeof action === "function") {
                        action();
                    } else {
                        setConfirmDialog(null);
                    }
                }}
            />
        </PageShell>
    );
}
